import { useState } from "react";
import { Box, Divider, Typography } from "@mui/material";
import { DotLegalHeader, DotLegalPrettyScrollbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation";
import { useBestPracticeCheckResultView } from "./BestPracticeCheckResultView.hooks";
import SummaryStatsBar from "./SummaryStatsBar";
import FindingCard from "./FindingCard";
import { BestPracticeCheckFindingType, BestPracticeCheckViewModel } from "@common/components/bestPracticeCheckDialog/BestPracticeCheckDialog.types.ts";

interface BestPracticeCheckResultViewProps {
    result: BestPracticeCheckViewModel;
    headerContent?: React.ReactNode;
}

function BestPracticeCheckResultView(props: BestPracticeCheckResultViewProps) {
    const { result, headerContent } = props;
    const { translateString } = useTranslation();
    const { getFindingTypeColor, getFindingTypeIcon, copyToClipboard } = useBestPracticeCheckResultView();
    const [selectedFindingType, setSelectedFindingType] = useState<BestPracticeCheckFindingType | undefined>(undefined);

    const filteredFindings =
        selectedFindingType !== undefined ? result.findings.filter((finding) => finding.findingType === selectedFindingType) : result.findings;

    const onFindingTypeClick = (findingType: BestPracticeCheckFindingType) => {
        setSelectedFindingType((current) => (current === findingType ? undefined : findingType));
    };

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: props.headerContent ? 0 : 2 }}>
                <Box sx={{ flex: 1 }}>
                    <SummaryStatsBar
                        result={result}
                        getFindingTypeColor={getFindingTypeColor}
                        translateString={translateString}
                        selectedFindingType={selectedFindingType}
                        onFindingTypeClick={onFindingTypeClick}
                    />
                </Box>
                {headerContent}
            </Box>
            <Divider sx={{ width: "100%", marginBottom: 2 }} />

            <Box sx={{ flex: 1, minHeight: 0, marginRight: -2 }}>
                <DotLegalPrettyScrollbar fullHeight>
                    <Box sx={{ paddingRight: 1 }}>
                        <Box sx={{ marginBottom: 3 }}>
                            <DotLegalHeader headerStyle="small">{translateString("executiveSummary")}</DotLegalHeader>
                            <Typography variant="body2">{result.executiveSummary}</Typography>
                        </Box>

                        <DotLegalHeader headerStyle="small">
                            {translateString("numberOfBestPracticeAnalyzeFindings", { count: filteredFindings.length })}
                        </DotLegalHeader>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 1 }}>
                            {filteredFindings.map((finding, index) => (
                                <FindingCard
                                    key={index}
                                    finding={finding}
                                    getFindingTypeColor={getFindingTypeColor}
                                    getFindingTypeIcon={getFindingTypeIcon}
                                    copyToClipboard={copyToClipboard}
                                />
                            ))}
                        </Box>
                    </Box>
                </DotLegalPrettyScrollbar>
            </Box>
        </Box>
    );
}

export default BestPracticeCheckResultView;
